import { collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

// ═══════════════ CREATE BOOKING ═══════════════
export async function createBooking(bookingData) {
  const createFn = httpsCallable(functions, 'createBooking');
  const result = await createFn(bookingData);
  return result.data; // { bookingId, referenceNumber, tripPin }
}

// ═══════════════ GET BOOKING ═══════════════
export async function getBooking(bookingId) {
  const snap = await getDoc(doc(db, 'bookings', bookingId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ═══════════════ GET RIDER BOOKINGS ═══════════════
export async function getRiderBookings(riderId, statusFilter = null) {
  let q = query(collection(db, 'bookings'), where('riderId', '==', riderId), orderBy('createdAt', 'desc'), limit(50));
  if (statusFilter) {
    q = query(collection(db, 'bookings'), where('riderId', '==', riderId), where('status', '==', statusFilter), orderBy('createdAt', 'desc'), limit(50));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ═══════════════ GET DRIVER BOOKINGS ═══════════════
export async function getDriverBookings(driverId, statusFilter = null) {
  let q = query(collection(db, 'bookings'), where('driverId', '==', driverId), orderBy('scheduledPickupTime', 'asc'), limit(50));
  if (statusFilter) {
    q = query(collection(db, 'bookings'), where('driverId', '==', driverId), where('status', '==', statusFilter), orderBy('scheduledPickupTime', 'asc'), limit(50));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ═══════════════ CANCEL BOOKING ═══════════════
export async function cancelBooking(bookingId, reason = '') {
  const cancelFn = httpsCallable(functions, 'cancelBooking');
  return (await cancelFn({ bookingId, reason })).data;
}

// ═══════════════ MODIFY BOOKING ═══════════════
export async function modifyBooking(bookingId, changes) {
  const modifyFn = httpsCallable(functions, 'modifyBooking');
  return (await modifyFn({ bookingId, changes })).data;
}

// ═══════════════ DRIVER CLAIM / DECLINE ═══════════════
export async function claimBooking(bookingId) {
  const fn = httpsCallable(functions, 'claimBooking');
  return (await fn({ bookingId })).data;
}

export async function declineTargetedOffer(bookingId, reason = '') {
  const fn = httpsCallable(functions, 'declineTargetedOffer');
  return (await fn({ bookingId, reason })).data;
}

export async function getAvailableBookingsForDriver() {
  const fn = httpsCallable(functions, 'getAvailableBookingsForDriver');
  return (await fn({})).data;
}

// DEPRECATED — alias to claimBooking
export async function acceptBooking(bookingId) {
  return claimBooking(bookingId);
}

// ═══════════════ VERIFY TRIP PIN ═══════════════
export async function verifyTripPin(bookingId, pin) {
  const verifyFn = httpsCallable(functions, 'verifyTripPin');
  return (await verifyFn({ bookingId, pin })).data;
}

// ═══════════════ UPDATE TRIP STATUS ═══════════════
export async function updateTripStatus(bookingId, newStatus, metadata = {}) {
  const updateFn = httpsCallable(functions, 'updateTripStatus');
  return (await updateFn({ bookingId, status: newStatus, ...metadata })).data;
}

// ═══════════════ MARK NO-SHOW ═══════════════
export async function markNoShow(bookingId) {
  const noShowFn = httpsCallable(functions, 'markNoShow');
  return (await noShowFn({ bookingId })).data;
}

// ═══════════════ GET QUALIFIED DRIVER COUNT ═══════════════
export async function getQualifiedDriverCount({ tier, passengerCount, hasMinor, childSeatRequired }) {
  const fn = httpsCallable(functions, 'findAvailableDrivers');
  return (await fn({ tier, passengerCount, hasMinor, childSeatRequired })).data;
}

// ═══════════════ GET QUALIFIED DRIVERS LIST ═══════════════
// Returns list of drivers eligible for a booking shape, for rider's preferred-driver picker.
export async function getQualifiedDriversList({ tier, passengerCount, hasMinor, childSeatRequired }) {
  const fn = httpsCallable(functions, 'getQualifiedDriversList');
  return (await fn({ tier, passengerCount, hasMinor, childSeatRequired })).data;
}

// DEPRECATED
export async function getAvailableDrivers(pickupTime, tier) {
  console.warn('getAvailableDrivers is deprecated. Use getQualifiedDriverCount.');
  const fn = httpsCallable(functions, 'findAvailableDrivers');
  return (await fn({ tier, passengerCount: 1 })).data;
}

// ═══════════════ GET PREFERRED DRIVERS ═══════════════
// Calls a cloud function that returns past drivers with ONLY public/masked fields.
// Returns: [{ id, displayName, plate, vehicleColour, vehicleMake, vehicleModel, rating, qualityTier, referenceNumber }]
export async function getPreferredDrivers(riderId) {
  const fn = httpsCallable(functions, 'getPreferredDriversForRider');
  const result = await fn({});
  return result.data?.drivers || [];
}

// ═══════════════ APPLY PROMO CODE ═══════════════
export async function applyPromoCode(code, riderId, tier, fareAmount) {
  const applyFn = httpsCallable(functions, 'applyPromoCode');
  return (await applyFn({ code, riderId, tier, fareAmount })).data;
}

// ═══════════════ RECURRING BOOKINGS (Batch 12) ═══════════════

// Create a recurring series. Returns immediately; fares are calculated asynchronously.
// Response: { success, seriesId, seriesReferenceNumber, occurrenceCount, childBookingIds, fareStatus }
export async function createRecurringBooking(recurringData) {
  const createFn = httpsCallable(functions, 'createRecurringBooking');
  return (await createFn(recurringData)).data;
}

// Cancel one specific occurrence within a series. Standard cancellation rules apply.
export async function cancelOccurrence(bookingId, reason = '') {
  const fn = httpsCallable(functions, 'cancelOccurrence');
  return (await fn({ bookingId, reason })).data;
}

// Cancel all future occurrences in a series.
// scope: 'all' (everything) or 'free' (only those >24hr away — no fees)
export async function cancelSeries(seriesId, reason = '', scope = 'all') {
  const fn = httpsCallable(functions, 'cancelSeries');
  return (await fn({ seriesId, reason, scope })).data;
}

// Modify all future occurrences in a series (address, time, etc.). Triggers re-pricing.
export async function modifySeries(seriesId, changes) {
  const fn = httpsCallable(functions, 'modifySeries');
  return (await fn({ seriesId, changes })).data;
}

// Modify a single occurrence (within a series). Triggers re-pricing for that occurrence only.
export async function modifyOccurrence(bookingId, changes) {
  const fn = httpsCallable(functions, 'modifyOccurrence');
  return (await fn({ bookingId, changes })).data;
}

// Get all series for the current rider.
export async function getRiderSeries() {
  const fn = httpsCallable(functions, 'getRiderSeries');
  return (await fn({})).data;
}

// Get a series + all its occurrences.
export async function getSeriesOccurrences(seriesId) {
  const fn = httpsCallable(functions, 'getSeriesOccurrences');
  return (await fn({ seriesId })).data;
}

// ═══════════════ MULTI-VEHICLE DISPATCH (Batch 13) ═══════════════

// Create a multi-vehicle booking. Returns immediately with group ID and child booking IDs.
// Drivers are matched in parallel via the existing pool mechanism.
// Response: { success, vehicleGroupId, vehicleGroupReferenceNumber, vehicleCount, childBookingIds, initialWindowExpiresAt, extendedWindowExpiresAt }
export async function createMultiVehicleBooking(data) {
  const fn = httpsCallable(functions, 'createMultiVehicleBooking');
  return (await fn(data)).data;
}

// Cancel an entire vehicle group (all child bookings).
export async function cancelVehicleGroup(vehicleGroupId, reason = '') {
  const fn = httpsCallable(functions, 'cancelVehicleGroup');
  return (await fn({ vehicleGroupId, reason })).data;
}

// Cancel a single vehicle within a group. Other vehicles continue.
export async function cancelOneVehicleInGroup(bookingId, reason = '') {
  const fn = httpsCallable(functions, 'cancelOneVehicleInGroup');
  return (await fn({ bookingId, reason })).data;
}

// Apply rider's decision when initial+extended matching window expires without all drivers confirmed.
// decision: 'wait' | 'proceed' | 'cancel_all'
export async function applyGroupConversionDecision(vehicleGroupId, decision) {
  const fn = httpsCallable(functions, 'applyGroupConversionDecision');
  return (await fn({ vehicleGroupId, decision })).data;
}

// Get live status of a vehicle group (parent + all child bookings).
export async function getVehicleGroupStatus(vehicleGroupId) {
  const fn = httpsCallable(functions, 'getVehicleGroupStatus');
  return (await fn({ vehicleGroupId })).data;
}

// Get all vehicle groups for the current rider.
export async function getRiderVehicleGroups() {
  const fn = httpsCallable(functions, 'getRiderVehicleGroups');
  return (await fn({})).data;
}
